import React, { useContext, useCallback } from 'react';
import isEqual from 'lodash/isEqual';
import { Listing } from '../../../../../models/Listing';
import api from '../../../../../api';
import { processLCWValuesBeforeSubmit } from '../../utils';
import PatchListingContexts from './PatchListing.context';
import { DeepPartial } from 'types';
import StepsContext from 'providers/Steps/Steps.context';
import ListingContext from '../Listing/Listing.context';
import mixpanelService from 'services/mixpanelService';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import { getMixpanelErrorProps } from 'axiosUtils';
import { ErrorViewedProps } from 'models/MixpanelEvents';

export type PatchListingProviderProps = {
  children?: React.ReactNode;
};

const PatchListingProvider: React.FC<PatchListingProviderProps> = (props) => {
  const { children } = props;

  const { listing, setListing } = useContext(ListingContext);
  const { currentStep } = useContext(StepsContext);

  const getMixpanelProps = useGetMixpanelProps();

  const patchListingValue = useCallback(
    async (values: DeepPartial<Listing>, updatedValues?: Partial<Listing>) => {
      if (!listing) return;

      const payload = {
        ...processLCWValuesBeforeSubmit(values),
        /**
         * StepsProvider derives currentStep from the pathname and mounts
         * before the step routes resolve, so it starts at -1 until the first
         * navigation. Storing that makes the next visit resume on a step
         * that does not exist.
         */
        ...(currentStep >= 0 ? { currentStep } : {}),
      };

      /**
       * Single gate for the whole form view: every patch merges back into the
       * listing, which rebuilds listingFormValues and reinitializes the form,
       * so any field that saves from an effect (revenueModel, the slug) sees a
       * fresh object and saves the same value again - one blur on any field
       * looped forever. Fields save from many call sites, so dropping the
       * no-op has to happen here rather than at each of them. Preview mode
       * gates the same way in pickChangedValues.
       */
      const hasChanges = Object.entries(payload).some(
        ([key, value]) =>
          !isEqual((listing as Record<string, any>)[key], value),
      );

      if (!hasChanges) return;

      try {
        await api.listings.patchListing(listing._id, payload);

        // Both views build their form from listingFormValues, which is derived
        // from this listing. Without merging the patched values back, switching
        // to preview mode reseeds from a stale copy and the edit disappears.
        setListing((old) => ({
          ...old,
          ...payload,
          ...updatedValues,
        }));
      } catch (e) {
        mixpanelService.errorViewed(
          getMixpanelProps(getMixpanelErrorProps(e)) as ErrorViewedProps,
        );
      }
    },
    [currentStep, listing, setListing, getMixpanelProps],
  );

  return (
    <PatchListingContexts.Provider
      value={{
        patchListingValue,
      }}
    >
      {children}
    </PatchListingContexts.Provider>
  );
};

export default PatchListingProvider;
